import React, { useState } from 'react';
import { useGetList, useNotify, useRefresh, useRecordContext } from 'react-admin';
import { gql } from 'apollo-boost';
import { Autocomplete, TextField, Box, Chip, Card, CardContent, Typography, Button } from '@mui/material';
import { client } from '../helpers/apolloClient';

// Linking goes through the finance-guarded mutation rather than the ordinary
// record update: the account holder reference is deliberately not settable
// through the generic path, so an admin cannot re-point a beneficiary at a
// different account behind finance's back.
const SET_ACCOUNT_HOLDER = gql`
    mutation setBeneficiaryAccountHolder($record: SetAccountHolderInput!) {
        setBeneficiaryAccountHolder(record: $record) { id accountHolderId }
    }
`;

const optionText = record =>
    `${record.registrationId || ''} - ${record.name || ''} ${record.fatherName || ''}`.trim();

// Beneficiaries reach an account holder from here rather than from the
// beneficiary's own edit form, which finance does not have access to. Siblings
// end up sharing one holder, which is the point: the bank details are typed once.
//
// The id comes from the Show record context, not from props: <Resource> renders
// the show component with no props, so a `props.id` here is always undefined -
// which made the linking query run unfiltered and list every beneficiary as if
// already linked.
export const AccountHolderBeneficiaries = () => {
    const record = useRecordContext();
    if (!record?.id) return null;
    return <AccountHolderBeneficiariesPanel holderId={record.id} />;
};

const AccountHolderBeneficiariesPanel = ({ holderId }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [choice, setChoice] = useState(null);

    const { data: linked = [] } = useGetList('Record', {
        filter: { accountHolder: holderId },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'registrationId', order: 'ASC' },
    });

    const { data: candidates = [] } = useGetList('Record', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'registrationId', order: 'ASC' },
    });

    // The backend filters on exact values only, so "not already linked to any
    // holder" has to be worked out here rather than asked of the server.
    const available = candidates.filter(r => !r.accountHolder);

    const save = (record, accountHolder, message) =>
        client
            .mutate({
                mutation: SET_ACCOUNT_HOLDER,
                variables: { record: { id: record.id, accountHolder } },
            })
            .then(() => { notify(message); refresh(); })
            .catch(() => notify(`Could not update beneficiaries: ${message.toLowerCase()}`, { type: 'warning' }));

    const attach = record => {
        if (!record) return;
        setChoice(null);
        save(record, holderId, 'Beneficiary linked to this account holder');
    };

    const detach = record => save(record, null, 'Beneficiary detached from this account holder');

    return (
        <Card sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Beneficiaries paid to this account holder
                </Typography>

                <Autocomplete
                    options={available}
                    getOptionLabel={optionText}
                    value={choice}
                    onChange={(e, value) => setChoice(value)}
                    onKeyDown={e => { if (e.key === 'Enter' && choice) attach(choice); }}
                    renderInput={params => (
                        <TextField {...params} label="Link a beneficiary" size="small" sx={{ my: 1, maxWidth: 480 }} />
                    )}
                />
                {choice && (
                    <Button variant="contained" size="small" onClick={() => attach(choice)} sx={{ mb: 2 }}>
                        Link
                    </Button>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {linked.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No beneficiaries are linked yet.
                        </Typography>
                    )}
                    {linked.map(record => (
                        <Chip
                            key={record.id}
                            label={optionText(record)}
                            onDelete={() => detach(record)}
                            deleteIcon={<span>&times;</span>}
                        />
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};

export default AccountHolderBeneficiaries;